import { type SchedulerDetailsTypes } from '@/components/shared/interview-scheduler/SchedulerDetails/SchedulerDetails.types';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';

import { schProcessInterviewerChanges } from '@/components/shared/interview-scheduler/SchedulerDetails/hooks/schProcessInterviewerChanges';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { useEffect, useRef, useState, useMemo } from 'react';

interface InterviewerData {
  availability?: { start: string; end: string }[];
  busyEvents?: any[];
  calenderConnected?: boolean;
  isZeroEvents?: boolean; // Indicates if calendar events fetched resulted in zero events
  cachedAt?: number;
}

const POLL_INTERVAL = 2000;
const MAX_RETRIES = 5;

const normalizeId = (id: string | number): string => String(id);

type PollMutationResponse = {
  data: {
    status: 'PENDING' | 'SUCCESS';
    results?: {
      events: Record<string, any[]>;
      users_info: Array<{ id: string | number; calendar_available: boolean }>;
    };
    // Added to carry variables through callbacks
    variables?: {
      fetch_uuid: string;
      weekRange: string;
      interviewers: (string | number)[];
    };
  };
  errors: any;
};

interface UseWeekScheduleFetchProps {
  isCurrentWeek?: boolean;
  weekRange: string;
  weekStartISO: string | null;
  weekEndISO: string | null;
  allInterviewerIds: (string | number)[]; // Expecting original types
  jobId?: number;
  timeZone?: string;
  isEnabled: boolean;
  interviewersData: Record<
    string,
    Record<string, SchedulerDetailsTypes.InterviewerData>
  >;
  setInterviewersData: React.Dispatch<
    React.SetStateAction<
      Record<string, Record<string, SchedulerDetailsTypes.InterviewerData>>
    >
  >;
  updateSharedData: (data: Record<string, any>) => void;
  tempCacheRef: React.MutableRefObject<
    Record<string, Record<string, SchedulerDetailsTypes.InterviewerData>>
  >;
  timeoutIdsRef: React.MutableRefObject<Record<string, NodeJS.Timeout>>;
  clearCache: (key?: string) => void;
  CACHE_TIMEOUT: number;
  interviewDataRef: React.MutableRefObject<any>;
  onFetchStart?: () => void;
  onFetchComplete?: (fetchedInterviewers: (string | number)[]) => void;
}

const ALLOWED_EVENTS_TO_SHOW_IN_UI = ['busy', 'oof', 'unknown'];

const errorMap = {
  primary_recruiter_calendar_not_connected:
    "Primary recruiter needs to connect their calendar to fetch interviewer's events",
  primary_recruiter_missing:
    "Add a primary recruiter to fetch interviewer's events",
  primary_recruiter_domain_mismatch:
    "Add a primary recruiter from the organization's domain to fetch interviewer's events",
};

export const useWeekScheduleFetch = ({
  isCurrentWeek,
  weekRange,
  weekStartISO,
  weekEndISO,
  allInterviewerIds,
  jobId,
  timeZone,
  isEnabled,
  interviewersData,
  setInterviewersData,
  updateSharedData,
  tempCacheRef,
  timeoutIdsRef,
  clearCache,
  CACHE_TIMEOUT,
  interviewDataRef,
  onFetchStart,
  onFetchComplete,
}: UseWeekScheduleFetchProps) => {
  const toast = useShowToast();

  // --- State and Refs ---
  const [isPollingActive, setIsPollingActive] = useState(false);
  const currentPollUuidRef = useRef<string | null>(null);
  const recentlyCompletedPollsRef = useRef<Record<string, number>>({});
  // pollingInterviewers tracks IDs *currently* involved in an active poll *initiated by this hook instance*
  const [pollingInterviewers, setPollingInterviewers] = useState<
    Set<string | number>
  >(new Set());
  const activePollingSessionsRef = useRef<
    Record<string, NodeJS.Timeout | boolean>
  >({});
  const activePollingData = useRef<Record<string, Set<string | number>>>({}); // Use ref as it doesn't need to trigger re-renders directly
  const successfullyFetchedInterviewersRef = useRef<Set<string | number>>(
    new Set()
  );
  const fetchStartedCalledRef = useRef(false);

  // Local tracking of IDs currently being fetched by *this hook instance*
  // This replaces the shared inProgressInterviewersRef for this instance's scope
  const instanceFetchingAvailabilityRef = useRef<Set<string | number>>(
    new Set()
  );
  const instanceFetchingEventIdsRef = useRef<Set<string | number>>(new Set());

  // --- Interviewer Processing ---
  // This now relies purely on data/cache state from schProcessInterviewerChanges
  const { toFetch: interviewersToFetch } = useMemo(() => {
    // Reset success tracking when the list of interviewers potentially changes
    successfullyFetchedInterviewersRef.current = new Set();
    const result = schProcessInterviewerChanges({
      currentWeekRange: weekRange,
      interviewersData, // Pass current state
      interviewerIds: allInterviewerIds, // Pass current required IDs
      tempCacheRef,
      timeoutIdsRef,
      // inProgressInterviewersRef, // REMOVED
      clearCache,
      setInterviewersData,
      CACHE_TIMEOUT,
      timeZone,
    });
    return result;
  }, [
    weekRange,
    interviewersData,
    allInterviewerIds,
    tempCacheRef,
    timeoutIdsRef,
    clearCache,
    setInterviewersData,
    CACHE_TIMEOUT,
    timeZone,
  ]);

  // --- Effect for onFetchStart ---
  useEffect(() => {
    if (
      isEnabled &&
      interviewersToFetch.length > 0 &&
      !fetchStartedCalledRef.current
    ) {
      onFetchStart?.();
      fetchStartedCalledRef.current = true;
    }
    if (!isEnabled || interviewersToFetch.length === 0) {
      fetchStartedCalledRef.current = false;
    }
  }, [isEnabled, interviewersToFetch, onFetchStart]);

  // --- Fetch Availability (React Query) ---
  const {
    // isLoading: isAvailabilityLoading,
    isFetching: isAvailabilityFetching,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  } = useQuery({
    queryKey: [
      'getInterviewerAvailability',
      weekRange,
      weekStartISO,
      JSON.stringify(
        [...interviewersToFetch].sort((a, b) =>
          String(a).localeCompare(String(b))
        )
      ),
    ],
    queryFn: async () => {
      if (!weekStartISO || interviewersToFetch.length === 0) {
        instanceFetchingAvailabilityRef.current.clear(); // Clear local tracking
        return null;
      }

      instanceFetchingAvailabilityRef.current = new Set(interviewersToFetch);

      try {
        const response = await InterviewDetailsAPI.getInterviewerAvailability({
          startDate: weekStartISO,
          timeZone,
          interviewerIds: interviewersToFetch, // Pass the calculated list
        });
        if (response?.interviewers) {
          setInterviewersData((prev) => {
            const weekData = prev[weekRange] || {};
            const updatedWeekData = { ...weekData };
            let changed = false;
            response.interviewers.forEach(
              ({ interviewer_id, interviewer_weekly_available_intervals }) => {
                const normalizedId = normalizeId(interviewer_id);
                // Ensure data is only updated if it actually changed
                const currentAvailability =
                  updatedWeekData[normalizedId]?.availability;
                const newAvailability =
                  interviewer_weekly_available_intervals.map(
                    ({ start, end }) => ({ start, end })
                  );

                // Deep comparison might be safer if interval order isn't guaranteed
                if (
                  JSON.stringify(currentAvailability) !==
                  JSON.stringify(newAvailability)
                ) {
                  updatedWeekData[normalizedId] = {
                    ...(updatedWeekData[normalizedId] || {}),
                    availability: newAvailability,
                  };
                  changed = true;
                }
              }
            );
            return changed ? { ...prev, [weekRange]: updatedWeekData } : prev;
          });
        }
        return response;
      } catch (error) {
        console.error(`[${weekRange}] Error fetching availability:`, error);
        throw error;
      } finally {
        // Clear local tracking for this query instance upon completion (success or error)
        instanceFetchingAvailabilityRef.current.clear();
      }
    },
    enabled:
      isEnabled &&
      interviewersToFetch.length > 0 &&
      !!weekStartISO &&
      !!weekRange &&
      timeZone !== 'local',
    staleTime: 0, // Keep stale time
    retry: false, // Keep retry policy
    // Prevent refetching automatically on window focus if data isn't stale
    refetchOnWindowFocus: false,
  });

  // --- Fetch Calendar Event IDs (React Query) ---
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  const eventIdQuery = useQuery({
    queryKey: [
      'getCalenderEventIds',
      weekRange,
      jobId,
      JSON.stringify(
        [...interviewersToFetch].sort((a, b) =>
          String(a).localeCompare(String(b))
        )
      ),
    ],
    queryFn: async () => {
      if (!weekStartISO || !weekEndISO || interviewersToFetch.length === 0) {
        instanceFetchingEventIdsRef.current.clear();
        return null;
      }

      instanceFetchingEventIdsRef.current = new Set(interviewersToFetch);

      try {
        const response = await InterviewDetailsAPI.getCalenderEventIds({
          from: weekStartISO,
          to: weekEndISO,
          time_zone: timeZone,
          user_ids: interviewersToFetch,
          ats_job_id: jobId,
        });

        if (response?.fetch_uuid) {
          const newFetchUuid = response.fetch_uuid;
          // Use the IDs that this query was initiated with
          const interviewersForNewPoll = Array.from(
            instanceFetchingEventIdsRef.current
          );

          // Cancel previous poll *initiated by this hook instance*
          if (
            currentPollUuidRef.current &&
            currentPollUuidRef.current !== newFetchUuid
          ) {
            const oldPollUuid = currentPollUuidRef.current;
            const oldTimeoutId = activePollingSessionsRef.current[oldPollUuid];
            if (typeof oldTimeoutId === 'number') clearTimeout(oldTimeoutId);
            delete activePollingSessionsRef.current[oldPollUuid];
            delete activePollingData.current[oldPollUuid]; // Clean up data ref
          }

          currentPollUuidRef.current = newFetchUuid;
          setIsPollingActive(true); // Mark polling as active for this instance
          const interviewersSet = new Set(interviewersForNewPoll);
          setPollingInterviewers(interviewersSet); // Set the IDs being polled *now*
          activePollingSessionsRef.current[newFetchUuid] = true; // Mark session active
          activePollingData.current[newFetchUuid] = interviewersSet; // Store IDs for this session

          pollMutation.mutate({
            fetch_uuid: newFetchUuid,
            weekRange, // Pass current weekRange
            interviewers: interviewersForNewPoll, // Pass IDs associated with this poll
          });
        } else {
          // No UUID, means no polling needed or instant result (handle if API supports this)
          // If no polling, consider these interviewers fetched (for the purpose of onFetchComplete)
          interviewersToFetch.forEach((id) =>
            successfullyFetchedInterviewersRef.current.add(id)
          );
          onFetchComplete?.(
            Array.from(successfullyFetchedInterviewersRef.current)
          );
        }
        return response;
      } catch (error) {
        console.error(
          `[${weekRange}] Error fetching calendar event IDs:`,
          error
        );
        // Don't clear state on error
        throw error; // Re-throw
      } finally {
        // Clear local tracking for this query instance
        instanceFetchingEventIdsRef.current.clear();
      }
    },
    enabled:
      isEnabled &&
      interviewersToFetch.length > 0 &&
      !!weekStartISO &&
      !!weekEndISO &&
      !!weekRange &&
      timeZone !== 'local' &&
      !isAvailabilityFetching,
    retry: 5,
    staleTime: 0, // Event IDs likely need to be fresh
  });

  // --- Polling Mutation (React Query) ---
  const pollMutation = useMutation<
    PollMutationResponse, // Expect response to include variables if needed in callbacks
    Error,
    { fetch_uuid: string; weekRange: string; interviewers: (string | number)[] }
  >({
    // @ts-expect-error -- pre-existing type debt (extraction move)
    mutationFn: async (variables) => {
      // Make sure this poll session is still considered active before calling API
      if (!activePollingSessionsRef.current[variables.fetch_uuid]) {
        console.warn(
          `[${variables.weekRange}] Poll ${variables.fetch_uuid} was cancelled before API call.`
        );
        throw new Error('Poll cancelled'); // Prevent API call if already cancelled
      }
      activePollingSessionsRef.current[variables.fetch_uuid] = true; // Mark as running API call
      const response = await InterviewDetailsAPI.getPollCalenderEvents({
        eventId: variables.fetch_uuid,
        jobId,
      });
      return { ...response, variables }; // Pass variables through
    },
    retry: MAX_RETRIES, // Use react-query's retry logic for the API call itself
    // Removed onMutate - inProgress added by eventIdQuery
    onSuccess: (response, variables) => {
      const {
        fetch_uuid,
        weekRange: pollWeekRange,
        interviewers: polledInterviewers,
      } = response?.data?.variables || variables;

      if (Array.isArray(response?.errors) && response?.errors.length > 0) {
        const error = response?.errors?.[0];

        const errorMsg =
          errorMap[error] ||
          (typeof error === 'string'
            ? error.includes(':')
              ? error.split(':')[1].trim()
              : error.replaceAll('_', ' ')
            : '');

        // Show toast only if the error is for the current week this hook instance is managing
        if (isCurrentWeek) {
          toast({
            title: 'Error fetching calendar events',
            description: errorMsg,
            status: 'message',
          });
        }

        // Stop loader and cleanup polling state for this failed poll
        onFetchComplete?.(polledInterviewers);

        const timeoutId = activePollingSessionsRef.current[fetch_uuid];
        if (typeof timeoutId === 'number') clearTimeout(timeoutId);
        delete activePollingSessionsRef.current[fetch_uuid];
        delete activePollingData.current[fetch_uuid];

        setPollingInterviewers((prev) => {
          const ns = new Set(prev);
          polledInterviewers.forEach((id) => ns.delete(id));
          return ns;
        });

        const hasActiveSessions =
          Object.keys(activePollingSessionsRef.current).length > 0;
        if (!hasActiveSessions) {
          setIsPollingActive(false);
        }
        if (currentPollUuidRef.current === fetch_uuid) {
          currentPollUuidRef.current = null;
        }
        return;
      }

      if (Array.isArray(response?.errors) && response?.errors.length > 0) {
        const error = response?.errors?.[0];

        const errorMsg =
          errorMap[error] || typeof error === 'string'
            ? error.includes(':')
              ? error.split(':')[1]
              : error.replaceAll('_', ' ')
            : '';

        // Show toast only if the error is for the current week this hook instance is managing
        if (isCurrentWeek) {
          toast({
            title: 'Error fetching calendar events',
            description: errorMsg,
            status: 'error',
            duration: 5000,
          });
        }

        // Stop loader and cleanup polling state for this failed poll
        onFetchComplete?.(polledInterviewers);

        const timeoutId = activePollingSessionsRef.current[fetch_uuid];
        if (typeof timeoutId === 'number') clearTimeout(timeoutId);
        delete activePollingSessionsRef.current[fetch_uuid];
        delete activePollingData.current[fetch_uuid];

        setPollingInterviewers((prev) => {
          const ns = new Set(prev);
          polledInterviewers.forEach((id) => ns.delete(id));
          return ns;
        });

        const hasActiveSessions =
          Object.keys(activePollingSessionsRef.current).length > 0;
        if (!hasActiveSessions) {
          setIsPollingActive(false);
        }
        if (currentPollUuidRef.current === fetch_uuid) {
          currentPollUuidRef.current = null;
        }
        return;
      }

      // todo: add error states and handle them, get copy from product

      // --- Relevance Check ---
      // 1. Is this poll still the "current" one for this hook instance?
      const isStillCurrentPollForInstance =
        currentPollUuidRef.current === fetch_uuid;
      // 2. Is the data for the correct week (relative to this hook instance)?
      const isStillCorrectWeekForInstance = weekRange === pollWeekRange;
      // 3. Which of the interviewers originally in *this* poll are *still* required by the parent?
      const latestRequiredIds = new Set(allInterviewerIds.map(normalizeId)); // Use normalized IDs for check
      const relevantPolledInterviewersInThisPoll = polledInterviewers.filter(
        (id) => latestRequiredIds.has(normalizeId(id))
      );

      // --- Stop Condition ---
      if (
        !isStillCurrentPollForInstance ||
        !isStillCorrectWeekForInstance ||
        relevantPolledInterviewersInThisPoll.length === 0
      ) {
        const timeoutId = activePollingSessionsRef.current[fetch_uuid];
        if (typeof timeoutId === 'number') clearTimeout(timeoutId);
        delete activePollingSessionsRef.current[fetch_uuid];
        delete activePollingData.current[fetch_uuid]; // Clean up data ref

        // Update polling state based on remaining active polls *for this instance*
        setPollingInterviewers((prev) => {
          const ns = new Set(prev);
          polledInterviewers.forEach((id) => ns.delete(id)); // Remove all associated with this finished poll
          return ns;
        });

        // Only set global polling active state based on remaining sessions
        const hasActiveSessions =
          Object.keys(activePollingSessionsRef.current).length > 0;
        if (!hasActiveSessions) {
          setIsPollingActive(false);
          currentPollUuidRef.current = null; // No active poll for this instance now
        } else if (
          !isStillCurrentPollForInstance &&
          currentPollUuidRef.current === fetch_uuid
        ) {
          // If this wasn't the current poll but somehow still held the ref, clear it
          currentPollUuidRef.current = null;
        }
        // Do not call onFetchComplete here, let the main logic handle completion status
        return;
      }

      // --- Handle PENDING ---
      if (response?.data?.status === 'PENDING') {
        // Continue polling only with the relevant interviewers
        const nextPollInterviewers = relevantPolledInterviewersInThisPoll; // Use original IDs
        setIsPollingActive(true); // Ensure it's marked active
        setPollingInterviewers(new Set(nextPollInterviewers)); // Update the set of actively polled users for this instance

        // Schedule next poll
        const timeoutId = setTimeout(() => {
          // Double-check context before mutating again
          if (
            currentPollUuidRef.current === fetch_uuid &&
            weekRange === pollWeekRange
          ) {
            pollMutation.mutate({
              fetch_uuid,
              weekRange: pollWeekRange,
              interviewers: nextPollInterviewers, // Poll only relevant ones
            });
          } else {
            // Cleanup if skipped
            delete activePollingSessionsRef.current[fetch_uuid];
            delete activePollingData.current[fetch_uuid];
            if (Object.keys(activePollingSessionsRef.current).length === 0)
              setIsPollingActive(false);
            if (currentPollUuidRef.current === fetch_uuid)
              currentPollUuidRef.current = null;
          }
        }, POLL_INTERVAL);
        activePollingSessionsRef.current[fetch_uuid] = timeoutId; // Store timeout ID
        return;
      }

      // --- Handle SUCCESS ---
      // Cleanup this poll session state
      delete activePollingSessionsRef.current[fetch_uuid];
      delete activePollingData.current[fetch_uuid];

      if (response?.data?.status === 'SUCCESS') {
        const results = response?.data?.results || {};
        // @ts-expect-error -- pre-existing type debt (extraction move)
        const { events = {}, users_info = [] } = results ?? {};
        recentlyCompletedPollsRef.current[fetch_uuid] = Date.now(); // Keep recently completed logic

        // --- Process results (existing logic seems okay, ensure using variables) ---
        // ... (updateSharedData logic using variables.weekRange)
        // ... (setInterviewersData logic using variables.weekRange and relevantPolledInterviewersInThisPoll)
        const userCalendarInfoUpdates: Record<string, any> = {};
        const userInfoMap: Record<string, any> = {};

        users_info.forEach((u) => {
          const userIdStr = normalizeId(u?.id);
          userInfoMap[userIdStr] = { ...u };
          const existingUserInfo =
            interviewDataRef.current?.userCalendarInfo?.[userIdStr] || {};
          userCalendarInfoUpdates[userIdStr] = {
            // ... (merge logic seems ok)
            id: u?.id,
            calendar_available: Boolean(u?.calendar_available),
            isCalendarConnected: Boolean(u?.calendar_available),
            zeroEventsByRange: {
              ...(existingUserInfo.zeroEventsByRange || {}),
              // Use the correct weekRange from the mutation variables
              [variables.weekRange]: (events[userIdStr] || []).length === 0,
            },
          };
        });

        if (Object.keys(userCalendarInfoUpdates).length > 0) {
          updateSharedData({
            userCalendarInfo: {
              ...(interviewDataRef.current?.userCalendarInfo || {}),
              ...userCalendarInfoUpdates,
            },
          });
        }

        setInterviewersData((prev) => {
          // Ensure we are updating the correct week based on the poll's context
          const targetWeekRange = variables.weekRange;
          if (
            !prev[targetWeekRange] &&
            relevantPolledInterviewersInThisPoll.length === 0
          )
            return prev;

          const weekData = prev[targetWeekRange] || {};
          const updatedWeekData = { ...weekData };
          let changesMade = false;

          // Iterate only over interviewers relevant to this poll and still required
          relevantPolledInterviewersInThisPoll.forEach((id) => {
            const userIdStr = normalizeId(id);
            const eventList = events[userIdStr] || [];
            const userInfo = userInfoMap[userIdStr]; // May be undefined if user_info was missing

            const newData: InterviewerData = {
              // Use interface
              // Preserve existing availability if present
              availability: updatedWeekData[userIdStr]?.availability,
              // Update event-related fields

              busyEvents: eventList.reduce((acc: any[], e: any) => {
                // if status is provided filter the events
                if (e && 'status' in e) {
                  if (ALLOWED_EVENTS_TO_SHOW_IN_UI.includes(e.status)) {
                    acc.push({ ...e, userInfo });
                  }
                } else if (e) {
                  acc.push({ ...e, userInfo });
                }
                return acc;
              }, []),
              calenderConnected: Boolean(userInfo?.calendar_available),
              isZeroEvents: eventList.length === 0,
            };

            // Only update if data actually changes
            if (
              JSON.stringify(updatedWeekData[userIdStr]) !==
              JSON.stringify(newData)
            ) {
              updatedWeekData[userIdStr] = newData;
              changesMade = true;
            }
          });

          return changesMade
            ? { ...prev, [targetWeekRange]: updatedWeekData }
            : prev;
        });

        // Mark these interviewers as successfully fetched for this cycle
        relevantPolledInterviewersInThisPoll.forEach((id) => {
          successfullyFetchedInterviewersRef.current.add(id);
        });
        onFetchComplete?.(
          Array.from(successfullyFetchedInterviewersRef.current)
        );
      } else {
        // Handle other non-PENDING statuses (e.g., FAILURE) as errors
        console.error(
          `[${pollWeekRange}] Polling failed/ended unexpectedly for ${fetch_uuid}. Status: ${response?.data?.status}`
        );
        // Don't show generic toast here, onError handles API errors
        // Consider if specific UI feedback is needed for API-reported failure vs. network error
      }

      // --- Post-Success/Failure Cleanup ---
      // Update polling state for this instance
      setPollingInterviewers((prev) => {
        const ns = new Set(prev);
        polledInterviewers.forEach((id) => ns.delete(id)); // Remove all associated with this finished poll
        return ns;
      });

      // Check if any polls are left for this instance
      const hasActiveSessions =
        Object.keys(activePollingSessionsRef.current).length > 0;
      if (!hasActiveSessions) {
        setIsPollingActive(false);
        // If this *was* the last active poll and it finished, clear the ref
        if (currentPollUuidRef.current === fetch_uuid) {
          currentPollUuidRef.current = null;
        }
      }
    },
    onError: (error, variables) => {
      const {
        fetch_uuid,
        interviewers: polledInterviewers,
        weekRange: pollWeekRange,
      } = variables;
      console.error(
        `[${pollWeekRange}] Poll mutation error for ${fetch_uuid}:`,
        error
      );

      // Avoid processing if error is due to manual cancellation
      if (error.message === 'Poll cancelled') return;

      // Ensure cleanup for this specific poll session
      const timeoutId = activePollingSessionsRef.current[fetch_uuid];
      if (typeof timeoutId === 'number') clearTimeout(timeoutId);
      delete activePollingSessionsRef.current[fetch_uuid];
      delete activePollingData.current[fetch_uuid];

      // Update polling state for this instance
      setPollingInterviewers((prev) => {
        const ns = new Set(prev);
        polledInterviewers.forEach((id) => ns.delete(id));
        return ns;
      });

      // Check if any polls are left for this instance
      const hasActiveSessions =
        Object.keys(activePollingSessionsRef.current).length > 0;
      if (!hasActiveSessions) {
        setIsPollingActive(false);
        if (currentPollUuidRef.current === fetch_uuid) {
          currentPollUuidRef.current = null;
        }
      }

      toast({
        title: `Failed to fetch calendar events for ${pollWeekRange}`,
        description: error.message,
        status: 'error',
      });
      // Do not call onFetchComplete on error, let the queries handle their error states
    },
    // onSettled can be simplified or removed as most logic is in onSuccess/onError/finally
  });

  // --- Effect for 'No Calendar Connected' Scenario ---
  // This effect seems okay, but ensure it uses the *current* allInterviewerIds prop
  useEffect(() => {
    if (isEnabled && allInterviewerIds.length > 0) {
      // Check isEnabled
      const userCalendarInfo = interviewDataRef.current?.userCalendarInfo || {};
      const relevantIds = allInterviewerIds; // Use the current prop
      const allUnavailable = relevantIds.every(
        (id) => userCalendarInfo[normalizeId(id)]?.calendar_available === false
      );

      if (allUnavailable) {
        // Stop polling *initiated by this instance*
        if (currentPollUuidRef.current) {
          const uuid = currentPollUuidRef.current;
          const timeoutId = activePollingSessionsRef.current[uuid];
          if (typeof timeoutId === 'number') clearTimeout(timeoutId);
          delete activePollingSessionsRef.current[uuid];
          delete activePollingData.current[uuid];
          currentPollUuidRef.current = null;
          setIsPollingActive(false);
          setPollingInterviewers(new Set());
        }
        // No need to clear inProgressInterviewersRef as it's removed
        // updateSharedData({ noSlotsAvailable: true }); // Potentially make this week-specific?
      } else {
        // updateSharedData({ noSlotsAvailable: false }); // Ensure it's reset if condition changes
      }
    } else {
      // updateSharedData({ noSlotsAvailable: false }); // Reset if no interviewers or disabled
    }
  }, [
    allInterviewerIds,
    interviewDataRef,
    weekRange,
    updateSharedData,
    isEnabled,
  ]); // Add isEnabled

  // --- Instance Cleanup Effect ---
  // This remains important for clearing timeouts when the hook unmounts or weekRange changes
  useEffect(() => {
    return () => {
      // Cancel all polls initiated by this hook instance
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.entries(activePollingSessionsRef.current).forEach(
        ([_uuid, timeoutId]) => {
          if (typeof timeoutId === 'number') {
            clearTimeout(timeoutId);
          }
          // No need to delete from ref here, as the whole ref will be garbage collected
        }
      );
      // Reset refs associated with this instance's polling state
      // No need to reset refs like activePollingSessionsRef, currentPollUuidRef as they belong to the unmounting instance.
    };
  }, [weekRange]); // Effect depends only on weekRange for instance identity

  // --- isLoading Calculation (Revised) ---
  const isLoading = useMemo(() => {
    // Is availability being fetched *by this instance* for the required IDs?
    const isFetchingAvailability =
      isAvailabilityFetching &&
      interviewersToFetch.some((id) =>
        instanceFetchingAvailabilityRef.current.has(id)
      );

    // Is event IDs being fetched *by this instance* for the required IDs?
    const isFetchingEventIds =
      eventIdQuery.isFetching &&
      interviewersToFetch.some((id) =>
        instanceFetchingEventIdsRef.current.has(id)
      );

    // Is polling active *for this instance* and involves any of the currently required IDs?
    const isActivelyPollingRelevant =
      isPollingActive &&
      interviewersToFetch.some((id) => pollingInterviewers.has(id));

    return (
      isFetchingAvailability || isFetchingEventIds || isActivelyPollingRelevant
    );
  }, [
    isAvailabilityFetching,
    eventIdQuery.isFetching,
    isPollingActive,
    pollingInterviewers,
    interviewersToFetch,
  ]);

  // --- Return Value ---
  return {
    isLoading,
    // Provide a set of interviewers currently being processed *by this instance*
    processingInterviewers: useMemo(() => {
      const processing = new Set<string | number>();
      // Add those being fetched by queries initiated by this instance
      instanceFetchingAvailabilityRef.current.forEach((id) =>
        processing.add(id)
      );
      instanceFetchingEventIdsRef.current.forEach((id) => processing.add(id));
      // Add those being actively polled by this instance
      pollingInterviewers.forEach((id) => processing.add(id));

      // Filter down to only those still required by `allInterviewerIds`
      const requiredSet = new Set(allInterviewerIds);
      return new Set(
        Array.from(processing).filter((id) => requiredSet.has(id))
      );
    }, [pollingInterviewers, allInterviewerIds]), // Depends on local polling state and required IDs
    interviewersToFetch, // Who needs data according to schProcessInterviewerChanges
    fetchedInterviewers: Array.from(successfullyFetchedInterviewersRef.current), // Who has successfully completed *in this cycle*
  };
};
